import axios from 'axios'

import { logService } from '@/services/log-service'
import { localStorageService, sessionStorageService } from '@/services/storage-service'

import { authService, axiosError, urlHelpers } from '.'
import { store } from '../redux/configureStore'
import { showSnackbar } from '../redux/ducks/snackbarDuck'

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

axios.defaults.headers = {
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
  Expires: '0',
}

axios.interceptors.request.use((request) => {
  const state = store?.getState()
  const jwtGuid = state?.app?.profile?.jwtGuid
  if (jwtGuid) {
    request.headers.jwtGuid = jwtGuid
  }
  return request
})

axios.interceptors.request.use((request) => {
  const jwtGuid = authService.getJwtGuid()
  if (jwtGuid) {
    request.headers.jwtGuid = jwtGuid
  }
  return request
})

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.config?.headers['X-ReCAPTCHAToken_V'] === 'V3' && error.response?.status === 506) {
      logService.warning('Google reCAPTCHA V3 failed, will try to switch to V2.')
      let response = ''

      try {
        window.grecaptcha.render('recaptcha', {
          sitekey: '6LeCs6MlAAAAANEnqDuTjqyE954zjz-Za9UqwIKb',
        })
      } catch (error) {
        logService.error('Error render reCAPTCHA V2', { error })
      }

      window.grecaptcha.execute()
      for (let i = 0; i < 120; i++) {
        response = window.grecaptcha.getResponse()
        if (response !== '' && response.length > 1) {
          logService.info('Got reCAPTCHA V2 response', { response, i })
          break
        }
        await sleep(1000)
      }

      if (response.length > 1) {
        error.config.headers['X-ReCAPTCHAToken'] = response
        error.config.headers['X-ReCAPTCHAToken_V'] = 'V2'
        logService.info('reCAPTCHA V2 was successful, retrying the request.')
        return axios.request(error.config)
      } else {
        logService.warning('No response from reCAPTCHA V2')
      }
    }

    if (error.response?.status === 500) {
      store.dispatch(
        showSnackbar({
          variant: 'failure',
          message: 'Something went wrong. Please try again later.',
        }),
      )
    }

    if (error.response?.status === 401) {
      logService.info('HTTP 401 URL', { error })
      localStorageService.removeJwtToken()
      sessionStorageService.removeJwtToken()
      if (!window.location.pathname.startsWith('/auth')) {
        window.location.replace('/')
      }
    }

    let errorDataObject = { ...error }
    if (errorDataObject?.config?.data) {
      try {
        errorDataObject.config.data = JSON.parse(errorDataObject.config.data)
        if (errorDataObject?.config?.data?.password) {
          errorDataObject.config.data.password = '******'
        }
      } catch (ex) {}
    }

    logService.error('HTTP error', { error })

    throw error
  },
)

const apiService = {
  get(url, params) {
    return axios({
      url: urlHelpers.getAbsoluteURL(url),
      method: 'get',
      headers: this.getAxiosConfig(),
      params,
    })
  },
  async post(url, body, params) {
    let reCAPTCHAToken, actionName
    if (url.indexOf('api/auth/') > -1) {
      actionName = url.replace(/\/?api\/auth\//, '')
      reCAPTCHAToken = await this.getCaptchaToken(actionName)
    } else if (url.indexOf('api/PayrollApproval/EmailRegister') > -1) {
      actionName = 'PayrollApproval'
      reCAPTCHAToken = await this.getCaptchaToken(actionName)
    }
    return axios({
      url: urlHelpers.getAbsoluteURL(url),
      method: 'post',
      data: body,
      headers: this.getAxiosConfig(reCAPTCHAToken, actionName),
      params,
    })
  },
  put(url, body, params) {
    return axios({
      url: urlHelpers.getAbsoluteURL(url),
      method: 'put',
      data: body,
      headers: this.getAxiosConfig(),
      params,
    })
  },
  delete(url, body, params) {
    return axios({
      headers: this.getAxiosConfig(),
      method: 'delete',
      data: body,
      url: urlHelpers.getAbsoluteURL(url),
      params,
    })
  },
  patch(url, body, params) {
    return axios({
      url: urlHelpers.getAbsoluteURL(url),
      method: 'patch',
      data: body,
      headers: this.getAxiosConfig(),
      params,
    })
  },
  async downloadFile(url, fileName, errorCallback, useApiFilename) {
    try {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ url }))
        return
      }
      const response = await axios({
        url: urlHelpers.getAbsoluteURL(url),
        method: 'GET',
        responseType: 'blob', // important
        headers: this.getAxiosConfig(),
      })
      const apiFilename = response?.headers?.['x-filename'] ?? fileName
      clickToDownloadFile(response, useApiFilename ? apiFilename : fileName)
    } catch (e) {
      axiosError.handleError(e, () => {
        if (errorCallback) {
          errorCallback('Internal Server Error. Please try again later.')
        }
      })
      throw e
    }
  },
  async downloadFileViaPost(url, body, fileName, errorCallback) {
    try {
      const response = await axios({
        url: urlHelpers.getAbsoluteURL(url),
        method: 'POST',
        data: body,
        responseType: 'blob', // important
        headers: this.getAxiosConfig(),
      })
      clickToDownloadFile(response, fileName)
    } catch (e) {
      axiosError.handleError(e, () => {
        errorCallback('Internal Server Error. Please try again later.')
      })
      throw e
    }
  },
  getAxiosConfig(reCAPTCHAToken, actionName) {
    const token = authService.getToken()
    const bfp = localStorageService.getBfp()
    const sfp = sessionStorageService.getSfp()
    return reCAPTCHAToken
      ? {
          Authorization: `Bearer ${token}`,
          'X-ReCAPTCHAToken': reCAPTCHAToken,
          'X-ReCAPTCHAToken_V': 'V3',
          'X-ReCAPTCHAToken_Action': actionName,
          bfp,
          sfp,
        }
      : {
          Authorization: `Bearer ${token}`,
          bfp,
          sfp,
        }
  },
  getCaptchaToken(action) {
    return new Promise((res, rej) => {
      try {
        window.grecaptcha.ready(function () {
          window.grecaptcha.execute('6LcTCwAVAAAAADYn_QkUxpU0IR0rAdr1RYae1Nfb', { action }).then(function (token) {
            res(token)
          })
        })
      } catch (error) {
        logService.error('Error getting captcha token', { error })
        res(`Error: ${error}`)
      }
    })
  },
  async getFile(url, fileName, errorCallback) {
    try {
      const response = await axios({
        url: urlHelpers.getAbsoluteURL(url),
        method: 'GET',
        responseType: 'blob', // important
        headers: this.getAxiosConfig(),
      })
      const fileUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      return fileUrl
    } catch (e) {
      axiosError.handleError(e, () => {
        if (errorCallback) {
          errorCallback('Internal Server Error. Please try again later.')
        }
      })
      throw e
    }
  },
}
export default apiService

function clickToDownloadFile(response, fileName) {
  const fileUrl = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = fileUrl
  link.setAttribute('download', fileName) //or any other extension
  document.body.appendChild(link)
  link.click()
  link.remove()
}
